/**
 * Dashboard Page
 * ==============
 * Protected analytics dashboard for authenticated users.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  TrendingUp, 
  Users, 
  Target, 
  Calendar,
  Bell,
  Settings,
  LogOut,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Download
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/shared/Button';
import { Card } from '../../components/shared/Card';
import { DASHBOARD_CONFIG, RESULTS_STATS } from '../../utils/constants';
import { formatNumber, formatPercent, formatCompact } from '../../utils/helpers';

/**
 * Dashboard Page Component
 * Analytics dashboard for authenticated users.
 * 
 * @returns {JSX.Element} Dashboard page
 */
function Dashboard() {
  const { user, logout } = useAuth();
  const [dateRange, setDateRange] = useState('7d');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  // Mock data for dashboard
  const metrics = [
    { 
      id: 'enrollments', 
      label: 'Total Enrollments', 
      value: 2847, 
      change: 12.5, 
      trend: 'up',
      icon: Users 
    },
    { 
      id: 'applications', 
      label: 'Applications', 
      value: 5234, 
      change: 8.2, 
      trend: 'up',
      icon: Target 
    },
    { 
      id: 'conversion', 
      label: 'Conversion Rate', 
      value: '54.3%', 
      change: -2.1, 
      trend: 'down',
      icon: TrendingUp 
    },
    { 
      id: 'retention', 
      label: 'Retention Rate', 
      value: '87.2%', 
      change: 3.4, 
      trend: 'up',
      icon: LayoutDashboard 
    },
  ];
  
  // Mock chart data
  const chartData = [
    { month: 'Jan', enrollments: 420, applications: 650 },
    { month: 'Feb', enrollments: 380, applications: 580 },
    { month: 'Mar', enrollments: 510, applications: 720 },
    { month: 'Apr', enrollments: 480, applications: 690 },
    { month: 'May', enrollments: 590, applications: 850 },
    { month: 'Jun', enrollments: 467, applications: 744 },
  ];
  
  // Mock recent activities
  const recentActivities = [
    { id: 1, action: 'New application received', time: '2 minutes ago', type: 'application' },
    { id: 2, action: 'Enrollment goal reached', time: '1 hour ago', type: 'milestone' },
    { id: 3, action: 'Report generated', time: '3 hours ago', type: 'report' },
    { id: 4, action: 'Integration synced', time: '5 hours ago', type: 'system' },
  ];
  
  return (
    <div className="min-h-screen bg-dme-gray-50">
      {/* Top Navigation */}
      <header className="bg-white border-b border-dme-gray-200 sticky top-0 z-30">
        <div className="flex items-center justify-between h-16 px-4 lg:px-8">
          {/* Left: Logo & Toggle */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-dme-blue to-dme-blue-dark flex items-center justify-center">
                <span className="text-white font-bold text-sm">D</span>
              </div>
              <span className="font-heading font-bold text-lg text-dme-blue-800 hidden sm:block">
                DME-CPH
              </span>
            </Link>
          </div>
          
          {/* Center: Date Range */}
          <div className="hidden md:flex items-center gap-2">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-dme-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-dme-orange focus:border-transparent outline-none"
            >
              {DASHBOARD_CONFIG.dateRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button className="p-2 text-dme-gray-500 hover:text-dme-blue hover:bg-dme-blue-50 rounded-lg transition-colors">
              <Bell className="w-5 h-5" />
            </button>
            <button className="p-2 text-dme-gray-500 hover:text-dme-blue hover:bg-dme-blue-50 rounded-lg transition-colors">
              <Settings className="w-5 h-5" />
            </button>
            <div className="w-px h-6 bg-dme-gray-200 mx-2" />
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-dme-orange flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <span className="text-sm font-medium text-dme-gray-700 hidden lg:block">
                {user?.name || 'User'}
              </span>
            </div>
            <button 
              onClick={logout}
              className="p-2 text-dme-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl lg:text-3xl text-dme-blue-800">
                Dashboard
              </h1>
              <p className="text-dme-gray-500 mt-1">
                Welcome back! Here's what's happening with your enrollment.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" leftIcon={<Filter className="w-4 h-4" />}>
                Filter
              </Button>
              <Button variant="primary" size="sm" leftIcon={<Download className="w-4 h-4" />}>
                Export
              </Button>
            </div>
          </div>
          
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
            {metrics.map((metric) => (
              <Card key={metric.id} variant="elevated" hoverable className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-dme-gray-500 mb-1">{metric.label}</p>
                    <p className="font-heading font-bold text-2xl lg:text-3xl text-dme-blue-800">
                      {typeof metric.value === 'number' 
                        ? formatCompact(metric.value) 
                        : metric.value
                      }
                    </p>
                    <div className={`flex items-center gap-1 mt-2 text-sm font-medium ${
                      metric.trend === 'up' ? 'text-dme-green' : 'text-red-500'
                    }`}>
                      {metric.trend === 'up' ? (
                        <ArrowUpRight className="w-4 h-4" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4" />
                      )}
                      <span>{Math.abs(metric.change)}%</span>
                      <span className="text-dme-gray-400 font-normal">vs last period</span>
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-dme-blue-50 flex items-center justify-center">
                    <metric.icon className="w-6 h-6 text-dme-blue" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          {/* Charts & Activity Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Main Chart */}
            <Card variant="elevated" className="lg:col-span-2 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-bold text-lg text-dme-blue-800">
                  Enrollment Trends
                </h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-dme-blue" />
                    <span className="text-sm text-dme-gray-500">Enrollments</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-dme-orange" />
                    <span className="text-sm text-dme-gray-500">Applications</span>
                  </div>
                </div>
              </div>
              
              {/* Simple Bar Chart */}
              <div className="h-64 flex items-end justify-between gap-4">
                {chartData.map((data, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex gap-1 items-end h-48">
                      <div 
                        className="flex-1 bg-dme-blue rounded-t transition-all duration-500"
                        style={{ height: `${(data.enrollments / 600) * 100}%` }}
                      />
                      <div 
                        className="flex-1 bg-dme-orange rounded-t transition-all duration-500"
                        style={{ height: `${(data.applications / 900) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-dme-gray-500">{data.month}</span>
                  </div>
                ))}
              </div>
            </Card>
            
            {/* Recent Activity */}
            <Card variant="elevated" className="p-6">
              <h3 className="font-heading font-bold text-lg text-dme-blue-800 mb-6">
                Recent Activity
              </h3>
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      activity.type === 'milestone' ? 'bg-dme-orange' :
                      activity.type === 'application' ? 'bg-dme-green' :
                      activity.type === 'report' ? 'bg-dme-blue' :
                      'bg-dme-gray-400'
                    }`} />
                    <div>
                      <p className="text-sm text-dme-gray-700">{activity.action}</p>
                      <p className="text-xs text-dme-gray-400">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="w-full mt-6 py-2 text-sm text-dme-orange font-medium hover:underline">
                View All Activity
              </button>
            </Card>
          </div>
          
          {/* Quick Links */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/demo">
              <Card variant="outlined" hoverable className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-dme-orange/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-dme-orange" />
                </div>
                <div>
                  <h4 className="font-semibold text-dme-blue-800">View Demo</h4>
                  <p className="text-sm text-dme-gray-500">Experience ADDI</p>
                </div>
              </Card>
            </Link>
            <Card variant="outlined" hoverable className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-dme-blue/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-dme-blue" />
              </div>
              <div>
                <h4 className="font-semibold text-dme-blue-800">Schedule Report</h4>
                <p className="text-sm text-dme-gray-500">Automated delivery</p>
              </div>
            </Card>
            <Card variant="outlined" hoverable className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-dme-green/10 flex items-center justify-center">
                <Settings className="w-6 h-6 text-dme-green" />
              </div>
              <div>
                <h4 className="font-semibold text-dme-blue-800">Settings</h4>
                <p className="text-sm text-dme-gray-500">Configure dashboard</p>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
